import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { BackofficeBusinessDetailed, BusinessAddress, BusinessContactInfo, BusinessActivity, PageProps } from '@/types'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { EditBusinessSheet } from '../../Businesses/sheets/EditBusinessSheet'
import { BusinessAddressSheet } from '../../../components/businesses/BusinessAddressSheet'
import { BusinessContactInfoSheet } from '../../../components/businesses/BusinessContactInfoSheet'
import { BusinessCountryOperationsSheet } from '../../../components/businesses/BusinessCountryOperationsSheet'
import { BusinessActivitySheet } from '../../../components/businesses/BusinessActivitySheet'
import {
  Briefcase, Building2, FileText, Users, ClipboardCheck, Mail, Package,
  Globe, Tag, PieChart, Settings, Pencil, Trash2, Plus, Copy, User,
} from 'lucide-react'


function EmptyRow({ message }: { message: string }) {
  return (
    <div className="px-4 py-6 text-center text-sm text-muted-foreground">{message}</div>
  )
}

type Props = {
  business: BackofficeBusinessDetailed
  countries: { id: string; code: string; name: string }[]
  states: { id: string; code: string; name: string; country_id: string }[]
}

export default function BackofficeBusinessesShow({ business, countries, states }: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.identity?.backoffice_admin ?? false
  const confirm = useConfirm()
  const [editOpen, setEditOpen] = useState(false)

  const [addressSheetOpen, setAddressSheetOpen] = useState(false)
  const [editAddress, setEditAddress] = useState<BusinessAddress | null>(null)

  const [contactInfoSheetOpen, setContactInfoSheetOpen] = useState(false)
  const [editContactInfo, setEditContactInfo] = useState<BusinessContactInfo | null>(null)

  const [countryOpsSheetOpen, setCountryOpsSheetOpen] = useState(false)

  const [activitySheetOpen, setActivitySheetOpen] = useState(false)
  const [editActivity, setEditActivity] = useState<BusinessActivity | null>(null)

  const [reassignmentModalOpen, setReassignmentModalOpen] = useState(false)
  const [reassignmentReason, setReassignmentReason] = useState('')
  const [isSubmittingReassignment, setIsSubmittingReassignment] = useState(false)

  async function handleDelete() {
    const ok = await confirm({
      title: 'Delete Business',
      description: `Delete "${business.name}"? This will also delete all associated services.`,
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (ok) {
      router.delete(`/backoffice/businesses/${business.id}`)
    }
  }

  async function handleReassignmentSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsSubmittingReassignment(true)
    router.post(
      `/backoffice/businesses/${business.id}/reassignment_request`, 
      { reason: reassignmentReason },
      { 
        onSuccess: () => {
          setReassignmentModalOpen(false)
          setReassignmentReason('')
        },
        onFinish: () => setIsSubmittingReassignment(false)
      }
    )
  }

  const isAssignedOfficer = auth.identity && (
    auth.identity.email_address === business.compliance_officer_email ||
    auth.identity.email_address === business.support_officer_email
  )

  return (
    <BackofficeLayout
      title={business.name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name },
      ]}
    >
      <EditBusinessSheet business={business} open={editOpen} onOpenChange={setEditOpen} basePath="/backoffice" />
      <BusinessAddressSheet
        open={addressSheetOpen}
        onOpenChange={setAddressSheetOpen}
        businessId={business.id}
        address={editAddress}
        basePath="/backoffice"
        countries={countries}
        states={states}
      />
      <BusinessContactInfoSheet
        open={contactInfoSheetOpen}
        onOpenChange={setContactInfoSheetOpen}
        businessId={business.id}
        contact={editContactInfo}
        basePath="/backoffice"
      />
      <BusinessCountryOperationsSheet
        open={countryOpsSheetOpen}
        onOpenChange={setCountryOpsSheetOpen}
        businessId={business.id}
        basePath="/backoffice"
        countries={countries}
        currentOperations={business.country_operations || []}
      />
      <BusinessActivitySheet
        open={activitySheetOpen}
        onOpenChange={setActivitySheetOpen}
        businessId={business.id}
        activity={editActivity}
        basePath="/backoffice"
      />

      <Dialog open={reassignmentModalOpen} onOpenChange={setReassignmentModalOpen}>
        <DialogContent>
          <form onSubmit={handleReassignmentSubmit}>
            <DialogHeader>
              <DialogTitle>Request Reassignment</DialogTitle>
              <DialogDescription>
                Provide a reason for why you need to be reassigned from this business. This will be sent to the administrators.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                placeholder="Optional reason..."
                value={reassignmentReason}
                onChange={(e) => setReassignmentReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReassignmentModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingReassignment}>
                {isSubmittingReassignment ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">{business.name}</h1>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                  <Trash2 className="w-3.5 h-3.5 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-sm text-muted-foreground font-mono">{business.email}</p>
          <p className="text-xs text-muted-foreground mt-1">Account: {business.account_name}</p>
        </div>

        {/* Customer Owner */}
        {business.customer_owner ? (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                Customer Owner
              </h2>
            </div>
            <div className="px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Name</span>
                <span className="text-sm font-medium">{business.customer_owner.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Email</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-mono">{business.customer_owner.email_address}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => navigator.clipboard.writeText(business.customer_owner!.email_address)}
                    title="Copy email"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={`mailto:${business.customer_owner.email_address}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title="Send email"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
              {business.customer_owner.multiple && (
                <p className="text-xs text-muted-foreground pt-1">
                  +{business.customer_owner.all!.length - 1} additional owner{business.customer_owner.all!.length > 2 ? 's' : ''}
                </p>
              )}
            </div>
          </section>
        ) : business.customer_owner === null && isAssignedOfficer ? (
          <section className="border rounded-lg border-amber-200 bg-amber-50/30">
            <div className="px-4 py-3 text-sm text-amber-700">
              No owner found for this workspace.{' '}
              <Link href={`/backoffice/accounts/${business.account_id}`} className="underline">
                Investigate
              </Link>
            </div>
          </section>
        ) : null}

        {/* Country Operations */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              Country Operations
            </h2>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCountryOpsSheetOpen(true)}>
              <Settings className="w-3.5 h-3.5 mr-1" />
              Manage
            </Button>
          </div>
          <div className="px-4">
            {business.country_operations?.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 py-3">
                {business.country_operations.map(co => (
                  <Badge key={co.id} variant="secondary" className="font-normal text-xs">{co.country_name}</Badge>
                ))}
              </div>
            ) : (
              <EmptyRow message="No operating countries" />
            )}
          </div>
        </section>

        {/* Activities */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Tag className="w-4 h-4 text-muted-foreground" />
              Business Activities
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setEditActivity(null); setActivitySheetOpen(true) }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add activity
            </Button>
          </div>
          <div className="px-4">
            {business.activities?.length > 0 ? (
              <div className="divide-y">
                {business.activities.map((act: BusinessActivity) => (
                  <div key={act.id} className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors">
                    <span className="text-sm text-foreground">{act.name}</span>
                    <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => { setEditActivity(act); setActivitySheetOpen(true) }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={async () => {
                          if (await confirm({ description: 'Remove this activity?', confirmLabel: 'Remove', destructive: true }))
                            router.delete(`/backoffice/businesses/${business.id}/activities/${act.id}`)
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyRow message="No business activities" />
            )}
          </div>
        </section>

        {/* Cap Table Summary */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <PieChart className="w-4 h-4 text-muted-foreground" />
              Cap Table Summary
            </h2>
            <Link
              href={`/backoffice/businesses/${business.id}/cap_table`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Manage Cap Table &rarr;
            </Link>
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Shareholders</span>
              <span className="text-sm text-foreground">{business.cap_table_shareholders_count}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Shares Outstanding</span>
              <span className="text-sm text-foreground">{business.cap_table_shares_outstanding.toLocaleString()}</span>
            </div>
          </div>
        </section>

        {/* Officers */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              Officers
            </h2>
            {isAdmin && (
              <Link
                href={`/backoffice/businesses/${business.id}/officer_assignments`}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Manage Officers →
              </Link>
            )}
          </div>
          {isAssignedOfficer && (
            <div className="px-4 py-2 border-b bg-muted/20 flex justify-between items-center">
              <span className="text-xs text-muted-foreground">You are assigned to this business.</span>
              <Button onClick={() => setReassignmentModalOpen(true)} variant="outline" size="sm" className="h-7 text-xs">
                Request Reassignment
              </Button>
            </div>
          )}
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Compliance</span>
              <span className="text-sm text-foreground">{business.compliance_officer_email || 'Not assigned'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Support</span>
              <span className="text-sm text-foreground">{business.support_officer_email || 'Not assigned'}</span>
            </div>
          </div>
        </section>

        {/* Addresses */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Addresses</h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setEditAddress(null); setAddressSheetOpen(true) }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add address
            </Button>
          </div>
          <div className="px-4">
            {business.addresses?.length > 0 ? (
              <div className="divide-y">
                {business.addresses.map((addr: BusinessAddress) => (
                  <div key={addr.id} className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors">
                    <div className="min-w-0">
                      {addr.label && <div className="text-xs text-muted-foreground mb-0.5">{addr.label}</div>}
                      <div className="text-sm font-medium text-foreground">{addr.street_address_1}</div>
                      {addr.street_address_2 && <div className="text-xs text-muted-foreground">{addr.street_address_2}</div>}
                      <div className="text-xs text-muted-foreground">{addr.city}{addr.state_province && `, ${addr.state_province}`} {addr.postal_code} · {addr.country_code}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => { setEditAddress(addr); setAddressSheetOpen(true) }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={async () => {
                          if (await confirm({ description: 'Remove this address?', confirmLabel: 'Remove', destructive: true }))
                            router.delete(`/backoffice/businesses/${business.id}/addresses/${addr.id}`)
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyRow message="No addresses" />
            )}
          </div>
        </section>

        {/* Contact Info */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Contact Information</h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => { setEditContactInfo(null); setContactInfoSheetOpen(true) }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add contact
            </Button>
          </div>
          <div className="px-4">
            {business.contact_infos?.length > 0 ? (
              <div className="divide-y">
                {business.contact_infos.map((ci: BusinessContactInfo) => (
                  <div key={ci.id} className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors">
                    <div className="min-w-0">
                      {ci.label && <div className="text-xs text-muted-foreground mb-0.5">{ci.label}</div>}
                      <div className="text-sm font-medium text-foreground">{ci.email}</div>
                      {ci.phone && <div className="text-xs text-muted-foreground">{ci.phone}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => { setEditContactInfo(ci); setContactInfoSheetOpen(true) }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={async () => {
                          if (await confirm({ description: 'Remove this contact?', confirmLabel: 'Remove', destructive: true }))
                            router.delete(`/backoffice/businesses/${business.id}/contact_infos/${ci.id}`)
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyRow message="No contact information" />
            )}
          </div>
        </section>

        {/* Inbound Emails */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              Inbound Emails
            </h2>
          </div>
          <Link
            href={`/backoffice/businesses/${business.id}/inbound_emails`}
            className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
          >
            <span className="text-sm text-foreground">
              {business.inbound_emails_count > 0
                ? `${business.inbound_emails_count} email${business.inbound_emails_count === 1 ? '' : 's'}`
                : 'No emails received yet'}
            </span>
            <span className="text-xs text-muted-foreground">View all →</span>
          </Link>
        </section>

        {/* Physical Mail */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Package className="w-4 h-4 text-muted-foreground" />
              Physical Mail
            </h2>
          </div>
          <Link
            href={`/backoffice/businesses/${business.id}/physical_mails`}
            className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
          >
            <span className="text-sm text-foreground">
              {business.physical_mails_count > 0
                ? `${business.physical_mails_count} item${business.physical_mails_count === 1 ? '' : 's'}`
                : 'No physical mail yet'}
            </span>
            <span className="text-xs text-muted-foreground">View all →</span>
          </Link>
        </section>

        {/* Formation */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              Formation
            </h2>
          </div>
          {business.formation ? (
            <Link
              href={`/backoffice/formations/${business.formation.id}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <div>
                <div className="text-sm font-medium text-foreground">{business.formation.legal_name}</div>
                {business.formation.entity_name && (
                  <div className="text-xs text-muted-foreground">{business.formation.entity_name}</div>
                )}
              </div>
              <StageBadge stage={business.formation.stage} variant="formation" />
            </Link>
          ) : (
            <EmptyRow message="No formation" />
          )}
        </section>

        {/* Tax Registrations */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Tax Registrations</h2>
          </div>
          {business.tax_registrations.length > 0 ? (
            <div>
              {business.tax_registrations.map((r, i) => (
                <Link
                  key={r.id}
                  href={`/backoffice/tax_registrations/${r.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{r.type_name}</div>
                      {r.jurisdiction_label && (
                        <div className="text-xs text-muted-foreground">{r.jurisdiction_label}</div>
                      )}
                    </div>
                  </div>
                  <StageBadge stage={r.stage} variant="formation" />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyRow message="No tax registrations" />
          )}
        </section>

        {/* Nominee Directors */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Nominee Directors</h2>
          </div>
          {business.nominee_directors.length > 0 ? (
            <div>
              {business.nominee_directors.map((nd, i) => (
                <Link
                  key={nd.id}
                  href={`/backoffice/nominee_directors/${nd.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">{nd.type_name}</span>
                  </div>
                  <StageBadge stage={nd.stage} variant="service" />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyRow message="No nominee directors" />
          )}
        </section>

        {/* Nominee Shareholders */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Nominee Shareholders</h2>
          </div>
          {business.nominee_shareholders.length > 0 ? (
            <div>
              {business.nominee_shareholders.map((ns, i) => (
                <Link
                  key={ns.id}
                  href={`/backoffice/nominee_shareholders/${ns.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">{ns.type_name}</span>
                  </div>
                  <StageBadge stage={ns.stage} variant="service" />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyRow message="No nominee shareholders" />
          )}
        </section>

        {/* Compliance Filings */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Compliance Filings</h2>
          </div>
          {business.compliance_filings.length > 0 ? (
            <div>
              {business.compliance_filings.map((cf, i) => (
                <Link
                  key={cf.id}
                  href={`/backoffice/compliance_filings/${cf.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-foreground">{cf.type_name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{cf.frequency.split('_').join('-')}</div>
                    </div>
                  </div>
                  <StatusBadge status={cf.status} />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyRow message="No compliance filings" />
          )}
        </section>
      </div>
    </BackofficeLayout>
  )
}
